import { Extension } from "@tiptap/core"
import { Paragraph } from "@tiptap/extension-paragraph"

const EMPTY_PARAGRAPH = /^ {0,3}<p\s*\/>[ \t]*(?:\n|$)/i
// Runs to the next blank line, like the CommonMark HTML block it would otherwise be.
const BREAK_FIRST_PARAGRAPH = /^ {0,3}(<br\s*\/?>[^\n]*(?:\n(?![ \t]*(?:\n|$))[^\n]*)*)(?:\n|$)/i

/**
 * Block-level paragraphs that marked would otherwise read as raw HTML blocks: `<p />` for an
 * empty paragraph, and a paragraph starting with `<br />`.
 *
 * The token gets its own type because marked appends a following paragraph's text to a
 * preceding `paragraph` token.
 */
const HtmlParagraphMarkdown = Extension.create({
  name: "htmlParagraphMarkdown",
  markdownTokenName: "htmlParagraph",
  markdownTokenizer: {
    name: "htmlParagraph",
    level: "block",
    // CommonMark doesn't let these interrupt a paragraph.
    start: () => -1,
    tokenize(src, _tokens, helpers) {
      const empty = EMPTY_PARAGRAPH.exec(src)
      if (empty) return { type: "htmlParagraph", raw: empty[0], tokens: [] }

      const breakFirst = BREAK_FIRST_PARAGRAPH.exec(src)
      if (breakFirst) {
        return {
          type: "htmlParagraph",
          raw: breakFirst[0],
          tokens: helpers.inlineTokens(breakFirst[1].trimEnd()),
        }
      }
      return
    },
  },
  parseMarkdown: (token, helpers) => {
    const content = helpers.parseInline(token.tokens ?? [])
    return helpers.createNode("paragraph", undefined, content.length ? content : undefined)
  },
})

// Blank lines only separate blocks in Markdown, so an empty paragraph needs a visible marker.
export const ParagraphContentExtension = Paragraph.extend({
  addExtensions: () => [HtmlParagraphMarkdown],
  renderMarkdown: (node, helpers) =>
    node.content?.length ? helpers.renderChildren(node.content) : "<p />",
})
